/**
 * FIXED: useTheme Hook - Using Theme Registry Instead of Context setTheme
 * 
 * Migrated from ThemeContext setTheme to theme registry/hook pattern
 * for better SSR support and FOUC prevention.
 */

import { useContext, useCallback, useEffect, useState } from 'react';
import { ThemeContext } from './ThemeProvider';
import type { ThemeId, ThemeChangeEvent } from './types';
import { 
  THEME_CHANGE_EVENT, 
  applyTheme, 
  saveTheme, 
  getSavedTheme,
  isValidThemeId,
  DEFAULT_THEME 
} from './theme-registry';

/**
 * Hook to access current theme (read-only from context)
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  
  if (!context) {
    throw new Error(
      'useTheme must be used within a ThemeProvider. ' +
      'Make sure to wrap your app with <ThemeProvider>.'
    );
  }
  
  // Return only read-only theme info, not setters
  return {
    themeId: context.themeId,
    availableThemes: context.availableThemes,
    isLoading: context.isLoading
  };
}

/**
 * FIXED: Hook for theme switching using registry instead of context
 * This prevents FOUC and works better with SSR
 */
export function useThemeSwitcher() {
  const { themeId: contextThemeId, isLoading } = useTheme();
  const [optimisticTheme, setOptimisticTheme] = useState<ThemeId | null>(null);
  
  const currentTheme = optimisticTheme || contextThemeId;
  
  const switchTheme = useCallback(async (newThemeId: ThemeId) => {
    if (!isValidThemeId(newThemeId) || newThemeId === currentTheme || isLoading) {
      return;
    }
    
    // Optimistic update for instant UI feedback
    setOptimisticTheme(newThemeId);
    
    try {
      // Apply theme using registry (no context mutation)
      applyTheme(newThemeId);
      saveTheme(newThemeId);
      
      // Reset optimistic state after successful application
      setTimeout(() => setOptimisticTheme(null), 50);
    } catch (error) {
      console.error('Failed to switch theme:', error);
      // Revert optimistic update on error
      setOptimisticTheme(null);
      // Revert to previous theme
      applyTheme(currentTheme);
    }
  }, [currentTheme, isLoading]);
  
  const toggleTheme = useCallback(() => {
    const newTheme = currentTheme === 'agi' ? 'dark' : 'agi';
    switchTheme(newTheme);
  }, [currentTheme, switchTheme]);
  
  return {
    currentTheme,
    switchTheme,
    toggleTheme,
    isLoading: isLoading || optimisticTheme !== null
  };
}

/**
 * Hook to listen for theme changes (useful for components outside React tree)
 */
export function useThemeListener(callback: (themeId: ThemeId) => void) {
  useEffect(() => {
    const handleThemeChange = (event: ThemeChangeEvent) => {
      callback(event.detail.themeId);
    };

    document.addEventListener(THEME_CHANGE_EVENT, handleThemeChange as EventListener);
    
    return () => {
      document.removeEventListener(THEME_CHANGE_EVENT, handleThemeChange as EventListener);
    };
  }, [callback]);
}

/**
 * Hook that returns the current theme without requiring ThemeProvider
 * Useful for SSR and preventing FOUC
 */
export function useCurrentTheme(): ThemeId {
  const [themeId, setThemeId] = useState<ThemeId>(() => {
    if (typeof window === 'undefined') return DEFAULT_THEME;
    
    // Read from DOM first (most reliable)
    const domTheme = document.documentElement.getAttribute('data-theme') as ThemeId;
    if (domTheme && isValidThemeId(domTheme)) {
      return domTheme;
    }
    
    // Fallback to localStorage
    return getSavedTheme();
  });

  useEffect(() => {
    const handleThemeChange = (event: ThemeChangeEvent) => {
      setThemeId(event.detail.themeId);
    };

    document.addEventListener(THEME_CHANGE_EVENT, handleThemeChange as EventListener);
    
    return () => {
      document.removeEventListener(THEME_CHANGE_EVENT, handleThemeChange as EventListener);
    };
  }, []);

  return themeId;
}

/**
 * Hook to get a CSS custom property value from the current theme
 */
export function useThemeToken(tokenName: string): string {
  const [value, setValue] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    return getComputedStyle(document.documentElement).getPropertyValue(tokenName).trim();
  });

  useEffect(() => {
    const updateValue = () => {
      if (typeof window === 'undefined') return;
      const newValue = getComputedStyle(document.documentElement).getPropertyValue(tokenName).trim();
      setValue(newValue);
    };

    // Update immediately
    updateValue();

    // Listen for theme changes  
    const handleThemeChange = () => updateValue();
    document.addEventListener(THEME_CHANGE_EVENT, handleThemeChange);
    
    return () => {
      document.removeEventListener(THEME_CHANGE_EVENT, handleThemeChange);
    };
  }, [tokenName]);

  return value;
}

/**
 * Hook to check if a specific theme is currently active
 */
export function useIsThemeActive(targetThemeId: ThemeId): boolean {
  const currentTheme = useCurrentTheme();
  return currentTheme === targetThemeId;
}

/**
 * SSR-safe hook to initialize theme on client-side mount
 * Prevents FOUC by applying saved theme immediately
 */
export function useThemeInitializer() {
  useEffect(() => {
    // Only run on client-side mount
    const savedTheme = getSavedTheme();
    const currentDOMTheme = document.documentElement.getAttribute('data-theme') as ThemeId;
    
    // Apply saved theme if different from DOM
    if (savedTheme !== currentDOMTheme) {
      applyTheme(savedTheme);
    }
  }, []);
}

/**
 * Hook for testing theme switches and FOUC behavior
 * Only available in development/test environments
 */
export function useThemeTestUtils() {
  if (import.meta.env.PROD) {
    throw new Error('useThemeTestUtils is only available in development/test environments');
  }
  
  const testThemeSwitch = useCallback(async (themeId: ThemeId) => {
    const startTime = performance.now();
    
    // Record initial state
    const initialTheme = document.documentElement.getAttribute('data-theme');
    
    // Switch theme
    applyTheme(themeId);
    
    const endTime = performance.now();
    const switchTime = endTime - startTime;
    
    return {
      success: document.documentElement.getAttribute('data-theme') === themeId,
      switchTime,
      initialTheme,
      finalTheme: themeId,
      hadFOUC: switchTime > 16 // > 1 frame indicates potential FOUC
    };
  }, []);
  
  const testSSRBehavior = useCallback(() => {
    // Simulate SSR by temporarily removing theme attribute
    const originalTheme = document.documentElement.getAttribute('data-theme');
    document.documentElement.removeAttribute('data-theme');
    
    const startTime = performance.now();
    
    // Re-initialize theme (simulates client hydration)
    const savedTheme = getSavedTheme();
    applyTheme(savedTheme);
    
    const endTime = performance.now();
    const hydrationTime = endTime - startTime;
    
    return {
      success: document.documentElement.getAttribute('data-theme') === savedTheme,
      hydrationTime,
      originalTheme,
      restoredTheme: savedTheme,
      hadFOUC: hydrationTime > 16
    };
  }, []);
  
  return {
    testThemeSwitch,
    testSSRBehavior
  };
}